export type BarPackage = 'open' | 'beerWine' | 'cocktails' | 'custom'
export type Intensity = 'light' | 'average' | 'heavy'
export type BudgetTier = 'budget' | 'standard' | 'luxury'
export type Season = 'summer' | 'winter'

export interface CalculatorInputs {
  guestCount: number
  hours: number
  drinkingPercent: number
  intensity: Intensity
  barPackage: BarPackage
  customWinePct: number
  customBeerPct: number
  customSpiritsPct: number
  champagneToast: boolean
  budgetTier: BudgetTier
  season: Season
  hotOutdoor: boolean
}

export const INTENSITY_FACTORS: Record<Intensity, number> = {
  light: 1,
  average: 1.25,
  heavy: 1.75,
}

export const BAR_PACKAGE_RATIOS: Record<Exclude<BarPackage, 'custom'>, { wine: number, beer: number, spirits: number }> = {
  open: { wine: 0.5, beer: 0.3, spirits: 0.2 },
  beerWine: { wine: 0.6, beer: 0.4, spirits: 0 },
  cocktails: { wine: 0.4, beer: 0.3, spirits: 0.3 },
}

export const BUDGET_PRICES: Record<BudgetTier, { wine: number, beer: number, spirit: number, label: string }> = {
  budget: { wine: 12, beer: 1.2, spirit: 20, label: 'Budget-Friendly / Bulk' },
  standard: { wine: 18, beer: 2.0, spirit: 35, label: 'Standard / Mid-Tier' },
  luxury: { wine: 35, beer: 3.5, spirit: 60, label: 'Luxury / Top-Shelf' },
}

export interface CalculatorResults {
  totalDrinks: number
  wineBottles: number
  wineCases: number
  redWineBottles: number
  whiteWineBottles: number
  beerServings: number
  beerCases: number
  spiritBottles: number
  champagneBottles: number
  iceLbs: number
  iceBags: number
  mixerLiters: number
  costLow: number
  costHigh: number
  wineCost: number
  beerCost: number
  spiritCost: number
  champagneCost: number
}

export function getRatios(inputs: CalculatorInputs) {
  if (inputs.barPackage === 'custom') {
    const total = inputs.customWinePct + inputs.customBeerPct + inputs.customSpiritsPct
    if (total <= 0) return { wine: 0, beer: 0, spirits: 0 }
    return {
      wine: inputs.customWinePct / total,
      beer: inputs.customBeerPct / total,
      spirits: inputs.customSpiritsPct / total,
    }
  }
  return BAR_PACKAGE_RATIOS[inputs.barPackage]
}

export function calculate(inputs: CalculatorInputs): CalculatorResults {
  const ratios = getRatios(inputs)
  const factor = INTENSITY_FACTORS[inputs.intensity]

  const totalDrinks = inputs.guestCount * (inputs.drinkingPercent / 100) * inputs.hours * factor

  const beerServings = totalDrinks * ratios.beer
  const beerCases = beerServings / 24

  const wineBottles = Math.ceil((totalDrinks * ratios.wine) / 5)
  const wineCases = Math.ceil(wineBottles / 12)

  const whitePct = inputs.season === 'summer' ? 0.6 : 0.4
  const whiteWineBottles = Math.round(wineBottles * whitePct)
  const redWineBottles = wineBottles - whiteWineBottles

  const spiritBottles = Math.ceil((totalDrinks * ratios.spirits) / 16.9)

  const champagneBottles = inputs.champagneToast ? Math.ceil(inputs.guestCount / 6) : 0

  const iceLbsPerGuest = inputs.hotOutdoor ? 2 : 1.5
  const iceLbs = inputs.guestCount * iceLbsPerGuest
  const iceBags = Math.ceil(iceLbs / 10)

  const mixerLiters = spiritBottles * 3

  const prices = BUDGET_PRICES[inputs.budgetTier]
  const wineCost = wineBottles * prices.wine
  const beerCost = Math.ceil(beerServings) * prices.beer
  const spiritCost = spiritBottles * prices.spirit
  const champagneCost = champagneBottles * (prices.wine * 1.1)

  const subtotal = wineCost + beerCost + spiritCost + champagneCost
  const costLow = Math.round(subtotal * 0.9)
  const costHigh = Math.round(subtotal * 1.15)

  return {
    totalDrinks: Math.round(totalDrinks),
    wineBottles,
    wineCases,
    redWineBottles,
    whiteWineBottles,
    beerServings: Math.ceil(beerServings),
    beerCases: Math.ceil(beerCases),
    spiritBottles,
    champagneBottles,
    iceLbs: Math.round(iceLbs),
    iceBags,
    mixerLiters: Math.round(mixerLiters),
    costLow,
    costHigh,
    wineCost: Math.round(wineCost),
    beerCost: Math.round(beerCost),
    spiritCost: Math.round(spiritCost),
    champagneCost: Math.round(champagneCost),
  }
}

export const DEFAULT_INPUTS: CalculatorInputs = {
  guestCount: 100,
  hours: 4,
  drinkingPercent: 85,
  intensity: 'average',
  barPackage: 'open',
  customWinePct: 50,
  customBeerPct: 30,
  customSpiritsPct: 20,
  champagneToast: true,
  budgetTier: 'standard',
  season: 'summer',
  hotOutdoor: false,
}

export function encodeInputsToParams(inputs: CalculatorInputs): string {
  const params = new URLSearchParams()
  Object.entries(inputs).forEach(([key, value]) => {
    params.set(key, String(value))
  })
  return params.toString()
}

export function decodeInputsFromParams(search: string): CalculatorInputs {
  const params = new URLSearchParams(search)
  const result: CalculatorInputs = { ...DEFAULT_INPUTS }
  if (!params.toString()) return result

  const num = (key: string, fallback: number) => {
    const v = params.get(key)
    return v !== null && !Number.isNaN(Number(v)) ? Number(v) : fallback
  }
  const bool = (key: string, fallback: boolean) => {
    const v = params.get(key)
    return v !== null ? v === 'true' : fallback
  }

  result.guestCount = num('guestCount', result.guestCount)
  result.hours = num('hours', result.hours)
  result.drinkingPercent = num('drinkingPercent', result.drinkingPercent)
  result.intensity = (params.get('intensity') as Intensity) || result.intensity
  result.barPackage = (params.get('barPackage') as BarPackage) || result.barPackage
  result.customWinePct = num('customWinePct', result.customWinePct)
  result.customBeerPct = num('customBeerPct', result.customBeerPct)
  result.customSpiritsPct = num('customSpiritsPct', result.customSpiritsPct)
  result.champagneToast = bool('champagneToast', result.champagneToast)
  result.budgetTier = (params.get('budgetTier') as BudgetTier) || result.budgetTier
  result.season = (params.get('season') as Season) || result.season
  result.hotOutdoor = bool('hotOutdoor', result.hotOutdoor)

  return result
}
